// src/pages/LandingPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/apiBase";
import { resolveUserRole } from "../utils/userRoles";
import "./LandingPage.css";

let introPlayedThisLoad = false;

function IntroSplashFirstVisit() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Play on every full page load/refresh, homepage only.
    // This won't replay when navigating away/back during the same SPA session.
    if (introPlayedThisLoad) return;
    introPlayedThisLoad = true;

    setVisible(true);
    const t = setTimeout(() => {
      setVisible(false);
    }, 2900);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const words = [
    { t: "story", top: "6%", left: "8%", d: "0.30s" },
    { t: "theme", top: "6%", left: "46%", d: "0.38s" },
    { t: "setting", top: "6%", left: "84%", d: "0.46s" },
    { t: "character", top: "22%", left: "8%", d: "0.54s" },
    { t: "metaphor", top: "22%", left: "88%", d: "0.62s" },
    { t: "plot", top: "46%", left: "6%", d: "0.70s" },
    { t: "symbolism", top: "46%", left: "92%", d: "0.78s" },
    { t: "dialogue", top: "70%", left: "10%", d: "0.86s" },
    { t: "imagery", top: "70%", left: "90%", d: "0.94s" },
    { t: "climax", top: "88%", left: "22%", d: "1.02s" },
    { t: "tone", top: "88%", left: "50%", d: "1.10s" },
    { t: "simile", top: "88%", left: "78%", d: "1.18s" },
    { t: "motif", top: "80%", left: "6%", d: "1.26s" },
    { t: "protagonist", top: "80%", left: "94%", d: "1.34s" },
    { t: "antagonist", top: "18%", left: "6%", d: "1.42s" },
    { t: "foreshadowing", top: "18%", left: "94%", d: "1.50s" },
    { t: "point of view", top: "46%", left: "50%", d: "1.58s" },
  ];

  return (
    <div className="intro-overlay">
      <div className="intro-card">
        <div className="intro-words" aria-hidden="true">
          {words.map((w, i) => (
            <span key={i} className="word" style={{ top: w.top, left: w.left, animationDelay: w.d }}>
              {w.t}
            </span>
          ))}
        </div>
        <div className="intro-book">
          <div className="cover left" />
          <div className="pages">
            <span className="line l1" />
            <span className="line l2" />
            <span className="line l3" />
          </div>
          <div className="cover right" />
        </div>
        <div className="intro-brand">
          <img
            src={process.env.PUBLIC_URL + "/hirilogo.png"}
            alt=""
            aria-hidden="true"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = process.env.PUBLIC_URL + "/logo512.png";
            }}
          />
          <div className="intro-text">How I Remember It</div>
        </div>
      </div>
    </div>
  );
}

function Hero({ onStart, onEnroll, backendStatus, statusLabel }) {
  const [typedDone, setTypedDone] = useState(false);
  const headRef = useRef(null);
  const heroTitle = "Creative Writing Essentials";
  const titleWords = heroTitle.split(" ");
  let charIndex = 0;

  useEffect(() => {
    try {
      if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        setTypedDone(true);
        return;
      }
    } catch {
      // ignore
    }

    const el = headRef.current;
    if (!el) return;
    const onEnd = (e) => {
      if (e && e.animationName === "ghostType") setTypedDone(true);
    };
    el.addEventListener("animationend", onEnd);
    return () => el.removeEventListener("animationend", onEnd);
  }, []);

  return (
    <section id="home" className="hero reveal is-visible">
      <div className="container hero-inner">
        <h1
          ref={headRef}
          className={`headline hero-title ${typedDone ? "typed-done" : ""}`}
          data-text={heroTitle}
          style={{ "--type-chars": 27, "--type-duration": "3.2s", "--type-delay": "0.6s" }}
        >
          {titleWords.map((word, wordIndex) => (
            <span key={word} className="hl-word">
              {Array.from(word).map((ch, i) => (
                <span key={`${word}-${i}`} className="hl" style={{ "--i": charIndex++ }}>
                  {ch}
                </span>
              ))}
              {wordIndex < titleWords.length - 1 ? (
                <span className="hl gap" aria-hidden="true" />
              ) : null}
            </span>
          ))}
        </h1>

        <p className="subhead">
          A guided, modern course by <strong>Miss Shaanzae Shahid</strong> to help you craft compelling stories with clarity,
          structure, and voice.
        </p>

        <div className="hero-ctas">
          <button type="button" className="btn btn-primary" onClick={onStart}>
            Enter Writing Studio
          </button>
          <button type="button" className="btn btn-ghost" onClick={onEnroll}>
            Enroll in HIRI
          </button>
          <p className="hero-cta-help">Sign in or continue your progress</p>
        </div>

        <div className={`wf-status ${backendStatus}`} aria-live="polite">
          {statusLabel[backendStatus]}
        </div>

        <ul className="hero-stats" aria-label="Course details">
          <li>
            <span className="stat">4 weeks</span>
            <span className="label">Duration</span>
          </li>
          <li>
            <span className="stat">Beginner–Intermediate</span>
            <span className="label">Level</span>
          </li>
          <li>
            <span className="stat">Hybrid</span>
            <span className="label">Online + Live</span>
          </li>
        </ul>
      </div>
    </section>
  );
}

function Instructor() {
  return (
    <section id="instructor" className="section section-instructor reveal-right">
      <div className="container grid two instructor">
        <div>
          <h2 className="section-title">Meet your instructor</h2>
          <div className="instructor-intro">
            <div className="instructor-photo" aria-hidden="true">
              SS
            </div>
            <div>
              <h3 className="instructor-name">Miss Shaanzae Shahid</h3>
              <p>
                A dedicated educator and writer focusing on storytelling, narrative structure, and the craft of revision.
                Expect practical lessons, supportive critique, and steady progress each week.
              </p>
              <p>
                You’ll learn how to plan stories, draft with confidence, and revise with clear next steps — building a voice
                that feels like yours.
              </p>
            </div>
          </div>
        </div>

        <div>
          <h2 className="section-title">What you get</h2>
          <div className="grid two">
            <div className="feedback-card">
              <div className="f-title">Clear weekly goals</div>
              <div className="f-note">Short lessons that build from idea → draft → revision.</div>
            </div>
            <div className="feedback-card">
              <div className="f-title">Actionable feedback</div>
              <div className="f-note">Supportive critique you can apply immediately.</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Highlights() {
  return (
    <section className="section reveal-left">
      <div className="container">
        <h2 className="section-title">What You Will Master</h2>
        <div className="grid three">
          <div className="card feature">
            <div className="feature-icon" aria-hidden="true">
              📖
            </div>
            <div>
              <h3>Build Strong Stories</h3>
              <p>Character, conflict, setting, and point of view — the building blocks that make stories work.</p>
            </div>
          </div>
          <div className="card feature">
            <div className="feature-icon" aria-hidden="true">
              ✍️
            </div>
            <div>
              <h3>Develop Your Style</h3>
              <p>Voice, rhythm, and language. Learn to revise drafts into clear, compelling prose.</p>
            </div>
          </div>
          <div className="card feature">
            <div className="feature-icon" aria-hidden="true">
              💬
            </div>
            <div>
              <h3>Grow with Feedback</h3>
              <p>Workshop-led critiques designed for growth with clear, actionable takeaways each week.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [backendStatus, setBackendStatus] = useState("checking");
  const [authUser, setAuthUser] = useState({ studentId: "", role: "student" });

  // Ping backend once when landing (keep existing functionality)
  useEffect(() => {
    async function checkBackend() {
      try {
        const res = await fetch(`${API_BASE_URL}/ping`);
        const text = await res.text();
        const data = JSON.parse(text);
        if (data?.status === "ok") setBackendStatus("online");
        else setBackendStatus("error");
      } catch (err) {
        setBackendStatus("offline");
      }
    }
    checkBackend();
  }, []);

  // Read login status from localStorage
  useEffect(() => {
    const sid = (localStorage.getItem("student_id") || "").toUpperCase();
    const role = resolveUserRole(sid, localStorage.getItem("role"));
    setAuthUser({ studentId: sid, role });
  }, []);

  useEffect(() => {
    // Previous project's reveal behavior (scoped to this page only)
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("is-visible");
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.1 }
    );

    document
      .querySelectorAll(".wf-home .reveal, .wf-home .reveal-left, .wf-home .reveal-right")
      .forEach((el) => io.observe(el));

    const onScroll = () => {
      const y = window.scrollY || 0;
      const p = Math.max(0, Math.min(1, y / 300));
      document.documentElement.style.setProperty("--hero-translate", `${p * 26}px`);
      document.documentElement.style.setProperty("--hero-opacity", `${1 - p * 0.45}`);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      io.disconnect();
      document.documentElement.style.removeProperty("--hero-translate");
      document.documentElement.style.removeProperty("--hero-opacity");
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("student_id");
    localStorage.removeItem("student_name");
    localStorage.removeItem("role");
    localStorage.removeItem("payment_made");
    setAuthUser({ studentId: "", role: "student" });
    navigate("/");
  };

  const onStart = () => {
    if (!authUser.studentId) {
      navigate("/login");
      return;
    }

    if (authUser.role === "tutor" || authUser.role === "admin") {
      navigate("/tutor");
      return;
    }

    navigate(`/student/${authUser.studentId}/dashboard_v2`);
  };

  const statusLabel = {
    online: "Backend Online",
    offline: "Backend Offline",
    checking: "Checking…",
    error: "Connection Error",
  };

  return (
    <div className="wf-home">
      <IntroSplashFirstVisit />

      <header className="site-header">
        <div className="container header-inner">
          <a className="brand" href="https://hiri.uk" aria-label="Hiri homepage">
            <img
              src={process.env.PUBLIC_URL + "/hirilogo.png"}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = process.env.PUBLIC_URL + "/logo512.png";
              }}
              alt="How I Remember It logo"
              className="brand-logo"
            />
            <span className="brand-text">How I Remember It</span>
          </a>

          <a className="btn btn-ghost nav-home" href="https://hiri.uk" aria-label="Home">
            Home
          </a>

          <nav className="nav">
            {authUser.studentId ? (
              <>
                <span className="home-id" title="Student ID">
                  {authUser.studentId}
                </span>
                <button type="button" className="btn btn-ghost" onClick={handleLogout}>
                  Log out
                </button>
              </>
            ) : (
              <button type="button" className="btn btn-primary" onClick={() => navigate("/login")}>
                Log in
              </button>
            )}
          </nav>
        </div>
      </header>

      <Hero onStart={onStart} onEnroll={() => navigate("/enroll")} backendStatus={backendStatus} statusLabel={statusLabel} />
      <Highlights />
      <Instructor />

      <footer className="site-footer">
        <div className="container footer-inner">
          <div className="brand-mini">
            <img
              src={process.env.PUBLIC_URL + "/hirilogo.png"}
              alt="How I Remember It logo"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = process.env.PUBLIC_URL + "/logo192.png";
              }}
            />
            <span>HOW I REMEMBER IT</span>
          </div>
          <small>© {new Date().getFullYear()} How I Remember It.</small>
        </div>
      </footer>
    </div>
  );
}
